import { hashString } from "@/lib/hash";
import type { ModuleId } from "@/router/routes";

import { siteStatus } from "../site/status";
import type { SiteRecord } from "../site/types";

import type { InboxNotification } from "../identity/console/types";
import type {
  ModuleStatus,
  Notification,
  NotificationKind,
  ProjectDetail,
  ProjectSummary,
} from "../types";
import { projectFileCount } from "./file-count";
import type { ServerDetail, ServerProject } from "./schemas";
import { studioStatus } from "./studio-status";

/**
 * The identity service's projects as the pages read them. Health, milestones, attention and activity come from
 * Studio and collaboration, which are not live yet: a live project shows no schedule and nothing waiting. Its stage
 * and Studio's status (open tasks) are the service's; its other modules show how a new project starts, as the mock
 * shows a project made in the wizard.
 */

/** Disciplines whose projects have no site to read (FR-SITE). */
const NO_SITE = new Set(["I", "E"]);

/** Site's status from what is known: the project's address, and the site record where it was read. */
function modules(project: ServerProject, site?: SiteRecord): ModuleStatus[] {
  return [
    ...(NO_SITE.has(project.discipline)
      ? []
      : [siteStatus({ record: site, hasAddress: project.address !== null })]),
    { module: "design", label: "Not started", tone: "neutral" },
    studioStatus(project.studioOpenTasks),
  ];
}

export function toSummary(project: ServerProject, site?: SiteRecord): ProjectSummary {
  return {
    id: project.id,
    shortId: project.shortId,
    name: project.name,
    client: project.client,
    discipline: project.discipline,
    type: project.type,
    city: project.city,
    state: project.state,
    stageIndex: project.stageIndex,
    health: { state: "no_schedule" },
    team: project.team,
    updatedAt: project.updatedAt,
    pinned: project.pinned,
    archived: project.archived,
    sample: project.sample !== null && project.sample !== undefined,
    ...(project.sample === null || project.sample === undefined
      ? {}
      : { sampleId: project.sample }),
    ...(project.drawingTemplate === undefined ? {} : { drawingTemplate: project.drawingTemplate }),
    attention: 0,
    modules: modules(project, site),
    coverSeed: hashString(project.id),
    access: project.access,
    canEdit: project.canEdit,
    canManage: project.canManage,
  };
}

/** What the detail adds to the project row: its stored files' count (drawings are added by `toDetail`) and its site record, each read from its own service. */
export interface DetailExtras {
  fileCount: number;
  site?: SiteRecord | undefined;
}

export function toDetail(project: ServerDetail, { fileCount, site }: DetailExtras): ProjectDetail {
  return {
    ...toSummary(project, site),
    ...(project.address === null ? {} : { address: project.address }),
    milestones: [],
    attentionItems: [],
    activity: [],
    drawings: project.drawings.map((drawing) => ({
      id: drawing.id,
      name: drawing.name,
      kind: drawing.kind,
      updatedAt: drawing.createdAt,
      drawingId: drawing.drawingId,
      restricted: drawing.restricted ?? false,
    })),
    fileCount: projectFileCount(project.drawings.length, fileCount),
  };
}

/** The inbox's kinds the notifications centre shows, by name; anything else reads as a comment. */
const INBOX_KINDS: readonly NotificationKind[] = [
  "mention",
  "assignment",
  "approval",
  "comment",
  "transmittal",
];

/** The module a notification's link opens: Studio's tasks and transmittals, else the drawing's comments in Design. */
function moduleOf(link: string | undefined): { module: ModuleId; tab?: string } {
  const studio = /\/studio\/([^/]+)/.exec(link ?? "");
  if (studio?.[1] !== undefined) return { module: "studio", tab: studio[1] };
  return { module: "design" };
}

/**
 * The inbox as the notifications centre lists it (FR-PROJ-080): the ones about a project the person sees, with its
 * name. Account notices with no project (a second-factor reset) are left out; the centre groups by project.
 */
export function toNotifications(
  inbox: readonly InboxNotification[],
  projects: readonly ServerProject[],
): Notification[] {
  const out: Notification[] = [];
  for (const item of inbox) {
    const project = projects.find((candidate) => candidate.id === item.projectId);
    if (project === undefined) continue;
    const kind = INBOX_KINDS.find((known) => known === item.kind) ?? "comment";
    out.push({
      id: item.id,
      projectId: project.id,
      projectName: project.name,
      kind,
      title: item.title,
      ...moduleOf(item.link),
      at: item.createdAt,
      read: item.read,
    });
  }
  return out;
}
